import { motion } from 'framer-motion';
import { CheckCircle, Clock, DollarSign, Phone, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import RecruitmentToStudyAbroadStrip from '@/components/common/RecruitmentToStudyAbroadStrip';
import { usePageMeta, useServiceSchema } from '@/hooks/useSEO';

const benefits = [
  'Work and live in the UK',
  'NHS healthcare access',
  'Path to settlement (ILR)',
  'Bring family members',
  'High earning potential',
  'Global career opportunities',
];

const salaryThresholds = [
  { category: 'Standard', salary: '£38,700/year' },
  { category: 'Shortage Occupation', salary: '£30,960/year' },
  { category: 'New Entrant', salary: '£30,960/year' },
  { category: 'PhD Relevant', salary: '£34,830/year' },
];

const UKSkilledPage = () => {
  usePageMeta(
    'UK Skilled Worker Visa from India 2026 | Sponsorship Jobs — Zafco HR (MEA Approved)',
    'Apply for UK Skilled Worker Visa via Zafco HR — India\'s MEA-approved overseas recruitment partner. NHS jobs, sponsor licence list, salary threshold £38,700, ILR pathway. Free eligibility check.'
  );
  useServiceSchema({
    name: 'UK Skilled Worker Visa Consulting & Sponsorship Job Placement',
    description:
      'UK Skilled Worker visa consulting — sponsor licence employer matching (NHS, hospitality, construction), salary threshold verification, ILR pathway planning.',
    serviceType: 'Immigration Consulting',
    areaServed: ['United Kingdom'],
    url: 'https://zafco.in/skilled/uk/uk-skilled-worker-visa',
  });
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20UK%20Skilled%20Worker%20visa', '_blank');
  };

  return (
    <div data-testid="uk-skilled-page">
      <PageHeader 
        title="UK Skilled Worker Visa 2026: Sponsorship Jobs & ILR Pathway"
        subtitle="Expert guidance for the UK Skilled Worker route — £38,700 salary threshold, NHS access, family inclusion, and 5-year settlement (ILR) pathway."
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'UK', path: '#' },
          { label: 'Skilled Worker Visa' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1563898058049-34334adf1001?w=1920"
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                UK Overseas Recruitment
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                UK Skilled Worker Visa Recruitment
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The UK Skilled Worker visa is the primary route for professionals seeking employment 
                in the United Kingdom. With competitive salaries and a path to settlement, it's an 
                excellent option for qualified Indian professionals.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Clock, label: 'Processing', value: '3-8 weeks' },
                  { icon: DollarSign, label: 'Min Salary', value: '£38,700' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl p-4">
                    <stat.icon className="w-6 h-6 text-zafco-teal mb-2" />
                    <p className="text-2xl font-bold text-zafco-blue">{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Salary Thresholds */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-zafco-blue to-zafco-teal text-white">
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-right">Min Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salaryThresholds.map((item) => (
                      <tr key={item.category} className="border-b">
                        <td className="px-4 py-3">{item.category}</td>
                        <td className="px-4 py-3 text-right font-bold text-zafco-blue">{item.salary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Check Your Eligibility
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free UK Visa Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6">
            Start Your UK Career Journey
          </h2>
          <Button 
            onClick={openWhatsApp}
            size="lg"
            className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
          >
            <Phone className="w-6 h-6 mr-3" />
            Get Free Assessment
          </Button>
        </div>
      </section>

      {/* Internal cross-link → Study Abroad cluster (SEO topical adjacency) */}
      <RecruitmentToStudyAbroadStrip
        country="the UK"
        studyHref="/study-abroad/uk/"
        visaHref="/study-abroad/uk/student-visa/"
        visaLabel="UK Student Visa Guide"
      />
    </div>
  );
};

export default UKSkilledPage;
